import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, delay, Observable, take } from 'rxjs';
import { CREATE_CART, GET_CART, GET_CHECKOUT_URL } from '../_graphql/cart';
import { CART_LINES_ADD, CART_LINES_UPDATE } from '../_graphql/cartLines';
import { ICartLineInput, ICartLineSave, ICartLineSellingPlanAllocation, ICartCost, IShopifyCart, IShopifyProductVariant } from '@app/_shared/_models';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ShoppingCartComponent } from '../_components/shopping-cart/shopping-cart.component';
import { Overlay } from '@angular/cdk/overlay';
import { GoogleAnalyticsService } from './google-analytics.service';
import { CART_ID } from '../_constants/local-storage-keys';
import { Location } from '@angular/common';
import { getSellingPlanAllocationsFromVariantNode } from '../_helpers/subscription-helpers';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../_helpers/platform.helpers';
import { BreakpointObserver } from '@angular/cdk/layout';
import { EXTRA_LARGE_BREAKPOINT, EXTRA_SMALL_BREAKPOINT, LARGE_BREAKPOINT, MEDIUM_BREAKPOINT, SMALL_BREAKPOINT } from '../_constants/breakpoints';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly location = inject<Location>(Location);
  private readonly matSnackBar = inject(MatSnackBar);
  private readonly apolloService = inject(Apollo);
  private readonly matDialog = inject(MatDialog);
  private readonly overlay = inject(Overlay);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  public cart$!: Observable<IShopifyCart | undefined>;
  private readonly _cart: BehaviorSubject<IShopifyCart | undefined>;
  private dialogRef: MatDialogRef<ShoppingCartComponent> | undefined = undefined;
  private isMobile = false;

  constructor() {
    this._cart = new BehaviorSubject<IShopifyCart | undefined>(undefined);
    this.cart$ = this._cart.asObservable();

    // Monitor Breakpoints to determine if the dialog should be full screen or not.
    this.breakpointObserver.observe([
      EXTRA_SMALL_BREAKPOINT,
      SMALL_BREAKPOINT,
      MEDIUM_BREAKPOINT,
      LARGE_BREAKPOINT,
      EXTRA_LARGE_BREAKPOINT
    ]).subscribe(state => {
      if (state.breakpoints[EXTRA_SMALL_BREAKPOINT] || state.breakpoints[SMALL_BREAKPOINT] || state.breakpoints[MEDIUM_BREAKPOINT] || state.breakpoints[LARGE_BREAKPOINT]) {
        if (this.dialogRef && this.dialogRef.getState() === 0 && !this.isMobile) {
          this.dialogRef.close();
          this.dialogRef = this.matDialog.open(ShoppingCartComponent, {
            scrollStrategy: this.overlay.scrollStrategies.block(),
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            maxHeight: '100vh'
          });
        }
        this.isMobile = true;
        } else {
          if (this.dialogRef && this.dialogRef.getState() === 0 && this.isMobile) {
            this.dialogRef.close();
            this.dialogRef = this.matDialog.open(ShoppingCartComponent, {
              scrollStrategy: this.overlay.scrollStrategies.block()
            });
          }
          this.isMobile = false;
        }
    });
  }

  //#region Public Actions
  public createCart(cart: IShopifyCart): void {
    this.saveCart(cart)
      .pipe(take(1))
      .subscribe((result: any) => {
        const cart = this.getCartFromQuery(result);
        this._cart.next(cart);
        if (cart?.id) {
          safeLocalStorageSetItem(CART_ID, cart.id);
        }
      });
  }
  public getCart(): void {
    const cartId = safeLocalStorageGetItem(CART_ID);

    if (cartId) {
      this.loadCart(cartId)
        .pipe(take(1))
        .subscribe((result: any) => {
          const cart = this.getCartFromQuery(result);
          this._cart.next(cart);
        });
    } else {
      const cart: IShopifyCart = {
        lines: []
      }
      this.createCart(cart);
    }
  }

  public getCartCheckoutUrl(): void {
    if (this._cart?.value?.id) {
      this.loadCartCheckoutUrl(this._cart.value.id)
      .pipe(take(1))
      .subscribe((result: any) => {
        if (result.data.cart.checkoutUrl) {
          location.href = result.data.cart.checkoutUrl;
        }
      });
    }
    this.googleAnalyticsService.beginCheckoutConversion(this._cart.value?.cost?.totalAmount);
    this.googleAnalyticsService.eventEmitter('Clicked Checkout', 'Shopping Cart', 'Clicked Checkout', 'Begin Checkout', 1);
  }

  public insertCartItem(variant: IShopifyProductVariant, productCount: number, sellingPlanId: string): void {
    if (this.dialogRef && this.dialogRef.getState() !== 0) {
      this.addedProductToCartNotification(`${variant.productTitle} has been added to your shopping cart.`);
    }

    const cartLineInput: Array<ICartLineSave> = [
      {
        merchandiseId: variant.id,
        quantity: productCount
      }
    ];

    if (sellingPlanId) {
      cartLineInput[0].sellingPlanId = sellingPlanId;
    }
    const cartId = safeLocalStorageGetItem(CART_ID);

    if (cartId) {
      this.addCartLines(cartId, cartLineInput)
      .pipe(take(1))
      .subscribe((result: any) => {
        const cart = this.getCartFromQuery(result);
        this._cart.next(cart);
      });
    }
  }

  public deleteCartItem(variant: IShopifyProductVariant, sellingPlanId: string, amountToRemove: number = 1): void {
    if (!variant.lineId) {
      this.matSnackBar.open('This item is not in your shopping cart.', 'Close', {duration: 3000, panelClass: ['snackbar-error']});
    }

    let quantity = 0;

    if (sellingPlanId) {
      const sellingPlanVariant = this._cart.value?.lines?.find((l: ICartLineInput) => l.sellingPlanAllocation?.sellingPlanId === sellingPlanId && l.variant?.id === variant.id)?.variant;
      if (sellingPlanVariant) {
        quantity = sellingPlanVariant.amountInCart;
      }
    } else {
      const noSellingPlanVariant = this._cart.value?.lines?.find((l: ICartLineInput) => l.variant?.id === variant.id && !l.sellingPlanAllocation)?.variant;
      if (noSellingPlanVariant) {
        quantity = noSellingPlanVariant.amountInCart;
      }
    }

    if (quantity > 0) {
      quantity -= amountToRemove;
    } else {
      quantity = 0;
    }

    const cartLineInput: Array<ICartLineSave> = [
      {
        id: variant.lineId,
        merchandiseId: variant.id,
        quantity
      }
    ];

    if (sellingPlanId) {
      cartLineInput[0].sellingPlanId = sellingPlanId;
    }
    const cartId = safeLocalStorageGetItem(CART_ID);

    if (cartId) {
      this.removeCartLines(cartId, cartLineInput)
      .pipe(take(1))
      .subscribe((result: any) => {
        const cart = this.getCartFromQuery(result);
        this._cart.next(cart);
        if (this.dialogRef && this.dialogRef.getState() !== 0) {
          this.addedProductToCartNotification(`${variant.productTitle} has been removed from your shopping cart.`);
        }
      });
    }

  }

  public openDialog(): void {
    if (this.isMobile) {
      this.dialogRef = this.matDialog.open(ShoppingCartComponent, {
        scrollStrategy: this.overlay.scrollStrategies.block(),
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh'
      });
    } else {
      this.dialogRef = this.matDialog.open(ShoppingCartComponent, {
        scrollStrategy: this.overlay.scrollStrategies.block()
      });
    }
    this.dialogRef.afterClosed().subscribe(result => {
    });
  }

  public closeDialog(): void {
    this.matDialog.closeAll();
  }
  //#endregion

  //#region Private Apollo Methods
  private saveCart(cart: IShopifyCart): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: CREATE_CART,
        variables: {
          cartInput: cart
        }
      })
      .pipe(take(1));
  }

  private addCartLines(cartId: string, lines: Array<ICartLineSave>): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: CART_LINES_ADD,
        variables: {
          cartId,
          lines
        }
      })
      .pipe(take(1));
  }

  private removeCartLines(cartId: string, lines: Array<ICartLineSave>): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: CART_LINES_UPDATE,
        variables: {
          cartId,
          lines
        }
      })
      .pipe(take(1));
  }

  private loadCartCheckoutUrl(id: string): Observable<any> {
    return this.apolloService
      .watchQuery({
        query: GET_CHECKOUT_URL,
        variables: {
          id
        }
      })
      .valueChanges.pipe(take(1));
  }

  private loadCart(id: string): Observable<any> {
    return this.apolloService
      .watchQuery({
        query: GET_CART,
        variables: {
          id
        }
      })
      .valueChanges.pipe(take(1));
  }
  //#endregion

  //#region Private Methods
  private addedProductToCartNotification(message: string): void {
    this.matSnackBar.open(message, 'View Cart', {duration: 3000, panelClass: ['snackbar-info']}).onAction()
      .pipe(delay(0))
      .subscribe(() => this.openDialog());
  }

  private getCartFromQuery(result: any): IShopifyCart | undefined {
    let cartRes = undefined;

    if (result.data?.cartCreate?.cart) {
      cartRes = result.data.cartCreate.cart;
    } else if (result.data?.cart) {
      cartRes = result.data.cart;
    } else if (result.data?.cartLinesAdd?.cart) {
      cartRes = result.data.cartLinesAdd.cart;
    } else if (result.data?.cartLinesUpdate?.cart) {
      cartRes = result.data.cartLinesUpdate.cart;
    } else {
      const cart: IShopifyCart = {
        lines: []
      }
      this.createCart(cart);
    }

    if (cartRes) {
      const cost: ICartCost = {
        subtotalAmount: +cartRes.cost.subtotalAmount?.amount,
        totalAmount: +cartRes.cost.totalAmount?.amount
      };

      const cart: IShopifyCart = {
        attributes: cartRes.attributes,
        id: cartRes.id,
        cost,
        lines: [],
        totalProducts: 0
      };

      if (cartRes.lines?.edges?.length > 0) {
        cartRes.lines?.edges.forEach((edge: any) => {
          cart.totalProducts += edge.node.quantity;

          const merch = edge.node.merchandise;

          const sellingPlanAllocations = getSellingPlanAllocationsFromVariantNode(merch);

          const variant: IShopifyProductVariant = {
            amountInCart: edge.node.quantity,
            availableForSale: merch.availableForSale,
            id: merch.id,
            image: merch.image,
            inventory: merch.quantityAvailable,
            price: +merch.price.amount,
            sku: merch.sku,
            productTitle: merch.product.title,
            variantTitle: merch.title,
            weight: merch.weight,
            weightUnit: merch.weightUnit,
            lineId: edge.node.id,
            sellingPlanAllocations
          }

          const line: ICartLineInput = {
            merchandiseId: merch.id,
            quantity: edge.node.quantity,
            id: edge.node.id,
            variant
          }

          if (edge.node?.sellingPlanAllocation) {
            const node = edge.node.sellingPlanAllocation;

            const priceAdjustments = node.priceAdjustments.map((pa: any) => {
              return {
                compareAtPrice: pa.compareAtPrice.amount,
                perDeliveryPrice: pa.perDeliveryPrice.amount,
                price: pa.price.amount
              }
            });

            const spa: ICartLineSellingPlanAllocation = {
              checkoutChargeAmount: node.checkoutChargeAmount.amount,
              remainingBalanceChargeAmount: node.remainingBalanceChargeAmount.amount,
              sellingPlanId: node.sellingPlan.id,
              name: node.sellingPlan.name,
              priceAdjustments
            }

            line.sellingPlanAllocation = spa;
          }

          cart.lines?.push(line);
        });
      }

      return cart;
    }

    return undefined;
  }
  //#endregion
}
